import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  CircleHelp,
  CalendarDays,
  Gauge,
  Home,
  Layers,
  LogOut,
  Menu,
  Moon,
  Phone,
  Settings,
  Smartphone,
  Sun,
  X,
} from 'lucide-react';

import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  EXPERT_MODE,
  STANDARD_MODE,
  useCgmDisplayMode,
} from '../contexts/CgmDisplayModeContext';

import '../soverdi-clone/styles/soverdiDemoR5.scoped.css';
import '../soverdi-clone/styles/cloneScope.css';

type NavigationItem = {
  key: string;
  descriptionKey: string;
  href: string;
  icon: React.ElementType;
  expertOnly?: boolean;
};

const navigation: NavigationItem[] = [
  { key: 'agrisafeR1:nav.dashboard', descriptionKey: 'agrisafeR1:nav.dashboardDesc', href: '/dashboard', icon: Home },
  { key: 'agrisafeR1:nav.sectors', descriptionKey: 'agrisafeR1:nav.pilotageDesc', href: '/sectors', icon: Layers },
  { key: 'agrisafeR1:journal.title', descriptionKey: 'agrisafeR1:journal.subtitle', href: '/irrigation-journal', icon: CalendarDays },
  { key: 'agrisafeR1:nav.pumping', descriptionKey: 'agrisafeR1:nav.pumpingDesc', href: '/pumping', icon: Gauge },
  { key: 'agrisafeR1:nav.equipment', descriptionKey: 'agrisafeR1:nav.equipmentDesc', href: '/devices', icon: Smartphone, expertOnly: true },
  { key: 'nav:alerts', descriptionKey: 'nav:alertsDesc', href: '/alerts', icon: AlertTriangle },
  { key: 'nav:analysis', descriptionKey: 'nav:analysisDesc', href: '/irrigation-history', icon: BarChart3, expertOnly: true },
  { key: 'nav:settings', descriptionKey: 'nav:settingsDesc', href: '/settings', icon: Settings, expertOnly: true },
  { key: 'nav:documentation', descriptionKey: 'nav:documentationDesc', href: '/documentation', icon: BookOpen },
  { key: 'nav:assistance', descriptionKey: 'nav:assistanceDesc', href: '/support', icon: CircleHelp },
  { key: 'nav:contact', descriptionKey: 'nav:contactDesc', href: '/contact', icon: Phone },
];

const isActive = (pathname: string, href: string): boolean => {
  if (pathname === href) return true;
  if (href === '/sectors' && pathname.startsWith('/sectors/')) return true;
  if (href === '/devices' && (pathname.startsWith('/devices/') || pathname.startsWith('/r6-soverdi-equipment/'))) return true;
  return false;
};

const CgmRuntimeChrome: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isExpert, setMode } = useCgmDisplayMode();
  const { t } = useTranslation(['agrisafeR1', 'nav', 'common']);
  const location = useLocation();

  const visibleNavigation = useMemo(
    () => navigation.filter((item) => !item.expertOnly || isExpert),
    [isExpert],
  );

  const current = useMemo(
    () => visibleNavigation.find((item) => isActive(location.pathname, item.href)) ?? visibleNavigation[0],
    [location.pathname, visibleNavigation],
  );

  if (!user) return <Navigate to="/login" replace />;

  const userLabel = user.name || user.email || '—';
  const normalizedRole = (user.role || '').trim().toLowerCase();
  let roleLabel: string;
  switch (normalizedRole) {
    case 'owner':
      roleLabel = t('agrisafeR1:nav.roleOwner');
      break;
    case 'farmer':
      roleLabel = t('agrisafeR1:nav.roleFarmer');
      break;
    case 'farm_manager':
      roleLabel = t('agrisafeR1:nav.roleFarmManager');
      break;
    case 'technician':
      roleLabel = t('agrisafeR1:nav.roleTechnician');
      break;
    case 'viewer':
      roleLabel = t('agrisafeR1:nav.roleViewer');
      break;
    default:
      roleLabel = t('agrisafeR1:nav.roleFarmManager');
  }

  return (
    <div className="App soverdi-r6 soverdi-r7 soverdi-clone-root min-h-screen bg-[var(--bg-primary)]">
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label={t('agrisafeR1:nav.closeMenu')}
        />
      )}

      <aside className={`fixed left-0 top-0 z-50 flex h-full w-72 flex-col border-r border-[var(--border-light)] bg-[var(--surface)] shadow-lg transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-[var(--border-light)] px-5">
          <BrandLogo size="md" showText={false} />
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg lg:hidden"
            aria-label={t('agrisafeR1:nav.closeMenu')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="shrink-0 border-b border-[var(--border-light)] px-5 py-4">
          <div className="truncate text-sm font-semibold text-[var(--text-primary)]" title={userLabel}>{userLabel}</div>
          <div className="mt-0.5 text-xs text-[var(--text-secondary)]">{roleLabel}</div>
          <div className="mt-3 inline-flex rounded-full bg-[var(--color-primary-50)] px-2.5 py-1 text-xs font-medium text-[var(--color-primary)]">
            {t('agrisafeR1:nav.dripBadge')}
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" aria-label={t('agrisafeR1:nav.openMenu')}>
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(location.pathname, item.href);
            return (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex min-h-[52px] items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${active ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'}`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{t(item.key)}</span>
                  <span className={`block truncate text-[11px] ${active ? 'text-white opacity-80' : 'text-[var(--text-tertiary)]'}`}>{t(item.descriptionKey)}</span>
                </span>
              </NavLink>
            );
          })}
        </nav>

        <div className="shrink-0 space-y-3 border-t border-[var(--border-light)] p-4">
          <button
            type="button"
            onClick={() => setMode(isExpert ? STANDARD_MODE : EXPERT_MODE)}
            className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border border-[var(--border-light)] px-3 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
            data-testid="cgm-display-mode-toggle"
          >
            <span>{t('common:cgmDisplayMode')}</span>
            <span className="font-semibold text-[var(--text-primary)]">
              {isExpert ? t('common:cgmDisplayExpert') : t('common:cgmDisplayStandard')}
            </span>
          </button>

          <div className="flex items-center justify-between gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={toggleTheme}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-[var(--border-light)] text-[var(--text-secondary)]"
              aria-label={t('nav:toggleTheme')}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-3 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
          >
            <LogOut className="h-4 w-4" />
            {t('nav:logout')}
          </button>
        </div>
      </aside>

      <div className="min-h-screen lg:pl-72">
        <header className="sticky top-0 z-30 flex min-h-16 items-center gap-3 border-b border-[var(--border-light)] bg-[var(--surface)]/95 px-4 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg lg:hidden"
            aria-label={t('agrisafeR1:nav.openMenu')}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <div className="truncate text-lg font-semibold text-[var(--text-primary)]">{t(current.key)}</div>
            <div className="truncate text-xs text-[var(--text-secondary)]">{t(current.descriptionKey)}</div>
          </div>
        </header>

        <main className="p-4 pb-24 sm:p-6 lg:pb-6">
          <Outlet />
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-[var(--border-light)] bg-[var(--surface)] px-1 pb-[env(safe-area-inset-bottom,0px)] lg:hidden">
        {visibleNavigation.slice(0, 5).map((item) => {
          const Icon = item.icon;
          const active = isActive(location.pathname, item.href);
          return (
            <NavLink key={item.href} to={item.href} className={`flex min-h-[64px] flex-col items-center justify-center gap-1 text-[10px] font-medium ${active ? 'text-[var(--color-primary)]' : 'text-[var(--text-tertiary)]'}`}>
              <Icon className="h-5 w-5" />
              <span className="max-w-full truncate px-1">{t(item.key)}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

const CgmRuntimeLayout: React.FC = () => <CgmRuntimeChrome />;

export default CgmRuntimeLayout;
